<script setup>
import { computed, onBeforeUnmount, onMounted, ref, shallowRef, useTemplateRef, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import StudioBlockMenu from '@/components/templateStudio/StudioBlockMenu.vue'
import StudioChartEditor from '@/components/templateStudio/StudioChartEditor.vue'
import StudioObjectEditor from '@/components/templateStudio/StudioObjectEditor.vue'
import StudioEditor from '@/components/templateStudio/StudioEditor.vue'
import StudioTableMenu from '@/components/templateStudio/StudioTableMenu.vue'
import StudioToolbar from '@/components/templateStudio/StudioToolbar.vue'
import { scrollParent } from '@/components/templateStudio/floatingMenu.js'
import { setStudioContext } from '@/components/templateStudio/studioContext.js'
import { buildPresentationCss } from '@/components/templateStudio/studioStylesheets.js'
import { stopChartEdit } from '@/components/templateStudio/charts/chartEditing.js'
import { useChartEditSession } from '@/composables/useChartEditSession.js'

// RED-317 §8 — one overview version (or the draft) on the Studio canvas: the
// Tiptap projection of its .docx fragment on a white page as wide as the
// fragment's text area, with the fragment's own styles.xml cascade injected.
// Charts and other Word-only content show as locked cards (they move
// unchanged on export). `editable` for the draft; its edits are emitted as a
// getter (see StudioEditor) for the drawer's autosave. Read-only, the page
// is scaled down to fit a narrower drawer instead of being cut off.
//
// Editing (PR 3b): the Studio toolbar above the page, the table and
// locked-card menus on it, pictures through `uploadPicture` (the draft media
// endpoint), and what the admin should know about a paste or a picture as an
// inline notice above the page (no toasts; nothing typed is ever undone).
// A drawn Word chart's data, titles and labels are edited in place (PR 3d-A,
// StudioChartEditor mounted into the chart's block; an Excel object's cells
// the same way through StudioObjectEditor, RED-317 PR 3d-B).
const props = defineProps({
  overviewId: { type: Number, required: true },
  document: { type: Object, required: true },
  editable: { type: Boolean, default: false },
  // (file) => Promise<{ ok, picture } | { ok: false, reason, message }>; editable only.
  uploadPicture: { type: Function, default: null },
})

const emit = defineEmits(['update'])

const { t } = useI18n()

const versionNumber = computed(() => props.document.version?.number ?? null)
// The sha the draft had when it was loaded: it keys the editor and the
// picture URLs, so an autosave (a new sha) neither remounts nor refetches.
const draftSha = computed(() => props.document.draft?.sha ?? null)

// The fragment's styles.xml cascade as one <style> element, removed with the canvas.
let styleElement = null
function applyPresentationCss(css) {
  if (!styleElement) {
    styleElement = window.document.createElement('style')
    styleElement.setAttribute('data-market-overview-canvas', '')
    window.document.head.appendChild(styleElement)
  }
  styleElement.textContent = css
}

// The Studio nodes read their labels, picture URLs and page geometry from a
// module-level context at render time, so it is set before the editor
// mounts (immediate + flush 'pre'), and the editor is keyed on the version.
watch(
  () => props.document,
  document => {
    setStudioContext({
      labels: {
        rawTitle: t('messages.admin.market_overviews.studio.rawTitle'),
        'rawHint.chart': t('messages.admin.market_overviews.studio.rawHint.chart'),
        'rawHint.table': t('messages.admin.market_overviews.studio.rawHint.table'),
        'rawHint.drawing': t('messages.admin.market_overviews.studio.rawHint.drawing'),
        'rawHint.field': t('messages.admin.market_overviews.studio.rawHint.field'),
        'rawHint.hyperlink': t('messages.admin.market_overviews.studio.rawHint.hyperlink'),
        'rawHint.sdt': t('messages.admin.market_overviews.studio.rawHint.sdt'),
        'rawHint.unknown': t('messages.admin.market_overviews.studio.rawHint.unknown'),
        rawEditHint: t('messages.admin.market_overviews.studio.rawEditHint'),
        chartUnsupported: t('messages.admin.market_overviews.studio.chartUnsupported'),
        'rawRun.textbox': t('messages.admin.market_overviews.studio.rawRun.textbox'),
        'rawRun.excel': t('messages.admin.market_overviews.studio.rawRun.excel'),
        'rawRun.object': t('messages.admin.market_overviews.studio.rawRun.object'),
        'rawRun.drawing': t('messages.admin.market_overviews.studio.rawRun.drawing'),
        'rawRun.previewUnavailable': t('messages.admin.market_overviews.studio.rawRun.previewUnavailable'),
        'rawRun.locked': t('messages.admin.market_overviews.studio.rawRun.locked'),
        pictureUploading: t('messages.admin.market_overviews.studio.pictureUploading'),
        pageBreak: t('messages.admin.market_overviews.studio.pageBreak'),
        codeUnknown: t('messages.admin.market_overviews.studio.codeUnknown'),
        imageUnavailable: t('messages.admin.market_overviews.studio.imageUnavailable'),
        blockFilledOnExport: t('messages.admin.market_overviews.studio.blockFilledOnExport'),
      },
      mediaUrl: mediaPart =>
        route('admin.market-overview.media', {
          marketOverview: props.overviewId,
          media: mediaPart.split('/').pop(),
          ...(draftSha.value ? { draft: 1, sha: draftSha.value.slice(0, 12) } : { version: versionNumber.value ?? undefined }),
        }),
      presentation: document.presentation ?? null,
      section: document.section ?? null,
      objectPreviews: document.previews ?? {},
      objectSheets: document.objects ?? { fonts: null, sheets: {} },
    })
    applyPresentationCss(buildPresentationCss(document.presentation ?? null))
  },
  { immediate: true, flush: 'pre' },
)

onBeforeUnmount(() => {
  styleElement?.remove()
  styleElement = null
})

/** The fragment's text area (page minus margins), so lines break where Word breaks them. */
const pageWidth = computed(() => {
  const section = props.document.section
  if (!section?.pageWidthMm) {
    return null
  }
  return `${Math.max(section.pageWidthMm - section.marginLeftMm - section.marginRightMm, 80)}mm`
})

/** Read-only: the page scaled to the frame's width (never up), so no line is cut off. */
const frame = useTemplateRef('frame')
const scale = ref(1)
const MM_TO_PX = 96 / 25.4
const PAGE_PADDING_PX = 48 // px-6 on both sides
const FRAME_PADDING_PX = 32 // p-4 on both sides
const fit = () => {
  const section = props.document.section
  if (props.editable || !frame.value || !section?.pageWidthMm) {
    scale.value = 1
    return
  }
  const textMm = Math.max(section.pageWidthMm - section.marginLeftMm - section.marginRightMm, 80)
  const natural = textMm * MM_TO_PX + PAGE_PADDING_PX
  scale.value = Math.min(1, (frame.value.clientWidth - FRAME_PADDING_PX) / natural)
}
let observer = null
onMounted(() => {
  observer = new ResizeObserver(fit)
  observer.observe(frame.value)
})
onBeforeUnmount(() => observer?.disconnect())
watch(() => [props.document, props.editable], fit)

const pageStyle = computed(() => ({
  ...(pageWidth.value ? { width: pageWidth.value, boxSizing: 'content-box' } : {}),
  ...(scale.value < 1 ? { zoom: scale.value } : {}),
}))

const editorKey = computed(() => `${props.overviewId}-${draftSha.value ? `draft-${draftSha.value}` : (versionNumber.value ?? 'x')}`)

const studio = useTemplateRef('studio')
const editorInstance = computed(() => (props.editable ? (studio.value?.editor ?? null) : null))

// The chart being edited inline, if any; leaving edit mode (Eelvaade) closes it.
const chartSession = useChartEditSession(() => studio.value?.editor ?? null)
watch(
  () => props.editable,
  (editable) => {
    if (!editable && studio.value?.editor) stopChartEdit(studio.value.editor)
  },
)

// The draft's editor, for the drawer's AI card (§9: the suggestion goes in at the selection or the cursor).
defineExpose({ editor: editorInstance })

// The table and card menus follow the drawer's scroll, not the window's.
const root = useTemplateRef('root')
const scrollTarget = shallowRef(null)
onMounted(() => {
  scrollTarget.value = scrollParent(root.value)
})

/** Inline notices above the page: { key, severity, text }, one per kind and text, closed by the admin. */
const notices = ref([])
const uploads = ref(0)

const notify = (notice) => {
  const text =
    notice.kind === 'picturesDropped'
      ? t('messages.admin.market_overviews.studio.picturesDropped', { count: notice.count })
      : (notice.message ?? t('messages.admin.market_overviews.studio.pictureFailed'))
  const key = `${notice.kind}:${text}`
  notices.value = [...notices.value.filter((item) => item.key !== key), { key, severity: notice.kind === 'picturesDropped' ? 'warn' : 'error', text }]
}

const dismiss = (key) => {
  notices.value = notices.value.filter((item) => item.key !== key)
}

// Read once, when the editor is created (StudioEditor's `options`).
const editorOptions = {
  paste: {
    uploadPicture: async (file) => {
      const result = props.uploadPicture ? await props.uploadPicture(file) : { ok: false, reason: 'failed', message: null }
      if (!result.ok && result.reason === 'too_large' && !result.message) {
        return { ...result, message: t('messages.admin.market_overviews.studio.pictureTooLarge') }
      }
      return result
    },
    onNotice: notify,
    onUploadsChange: (count) => {
      uploads.value = count
    },
  },
}
</script>

<template>
  <!-- Editing, the page takes its full height and the drawer scrolls (as read-only), with the toolbar and the notices kept on top. -->
  <div ref="root" class="flex flex-col gap-2" :class="{ 'shrink-0': editable }">
    <div v-if="editorInstance" class="sticky top-0 z-20 flex flex-col items-center gap-2">
      <StudioToolbar :editor="editorInstance" :uploading="uploads > 0" />
      <Message
        v-for="notice in notices"
        :key="notice.key"
        :severity="notice.severity"
        size="small"
        closable
        class="w-full"
        data-testid="mo-canvas-notice"
        @close="dismiss(notice.key)"
      >
        {{ notice.text }}
      </Message>
    </div>
    <div ref="frame" class="overflow-x-auto rounded-md bg-gray-100 p-4" :class="{ 'ring-1 ring-primary-200': editable }" data-testid="mo-canvas">
      <div class="studio-page mx-auto px-6 py-5" :style="pageStyle">
        <div class="studio-doc" :data-testid="editable ? 'mo-editor' : undefined">
          <StudioEditor
            ref="studio"
            :key="editorKey"
            :doc="document.doc"
            :editable="editable"
            :options="editable ? editorOptions : null"
            @update="getter => emit('update', getter)"
          />
        </div>
      </div>
    </div>
    <template v-if="editorInstance && scrollTarget">
      <StudioTableMenu :editor="editorInstance" :scroll-target="scrollTarget" />
      <StudioBlockMenu :editor="editorInstance" :scroll-target="scrollTarget" />
    </template>
    <StudioChartEditor v-if="editorInstance && chartSession && chartSession.controller.kind !== 'object'" :key="chartSession.id" :editor="editorInstance" :controller="chartSession.controller" />
    <StudioObjectEditor v-if="editorInstance && chartSession && chartSession.controller.kind === 'object'" :key="chartSession.id" :editor="editorInstance" :controller="chartSession.controller" />
  </div>
</template>
